import { extendVariants, Textarea } from '@nextui-org/react'
import { VscArrowUp } from '@react-icons/all-files/vsc/VscArrowUp'
import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSimpleTextareaClassName } from '~/hooks'
import { I18nKey } from '~/i18n/declaration'
import { cm } from '~/utils/utils'

export const CurieTextarea = extendVariants(Textarea, {
  variants: {
    color: {
      stone: {
        inputWrapper: [
          'data-[hover=true]:bg-white dark:data-[hover=true]:bg-medium7',
          'group-data-[focus=true]:ring-gray-300 dark:group-data-[focus=true]:ring-gray-500',
          'group-data-[focus-visible=true]:ring-1',
          'group-data-[focus-visible=true]:ring-offset-0',
          'group-data-[focus=true]:bg-white dark:group-data-[focus=true]:bg-medium7',
          'bg-white dark:bg-medium7',
          'rounded-xl',
          'ring-1 ring-inset ring-gray-300 dark:ring-gray-700 shadow-sm',
          'p-[1px]',
          'overflow-hidden',
          'data-[disabled=true]:cursor-not-allowed'
        ],
        input: [
          'py-[10px] pr-16 pl-2 text-black dark:text-white ml-1',
          'data-[disabled=true]:cursor-not-allowed'
        ]
      }
    }
  },
  defaultVariants: {
    color: 'stone',
    textSize: 'base',
    removeLabel: true
  }
})

interface ChatInputProps {
  disabled?: boolean
  onSendMessage: (message: string) => void
  initialMessage?: string
}

function ChatInput({ disabled = false, onSendMessage, initialMessage = '' }: ChatInputProps) {
  const { t } = useTranslation()
  const [message, setMessage] = useState<string>(initialMessage || '')
  const input = useRef<HTMLTextAreaElement | null>(null)
  const allowSend = !disabled && message.trim()

  useEffect(() => {
    input?.current?.focus()
  }, [])

  const handleSendChatMessage = async () => {
    if (allowSend) {
      setMessage('')
      onSendMessage(message)
    }
  }

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault() // prevent a new line
      handleSendChatMessage()
    }
  }
  const { ref: chatInputRef, className: chatInputClassName } = useSimpleTextareaClassName({
    initialHeight: 40
  })

  return (
    <div
      className={cm(
        'w-full flex justify-center flex-col items-center h-full'
        // isTerminated(taskStatus) ? 'pb-[25px]' : 'pb-[45px]'
      )}
    >
      <div
        className={cm(
          'w-full relative text-base flex md:max-w-3xl lg:max-w-[40rem] xl:max-w-[48rem]'
        )}
      >
        <CurieTextarea
          aria-label='Chat Input'
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={onKeyPress}
          disabled={disabled}
          placeholder={t(I18nKey.CHAT_INTERFACE$INPUT_PLACEHOLDER)}
          className={cm('select-none px-3 outline-0', chatInputClassName)}
          ref={ref => {
            input.current = ref as unknown as HTMLTextAreaElement
            chatInputRef.current = ref as unknown as HTMLTextAreaElement
          }}
          maxRows={10}
          minRows={1}
        />

        <button
          type='button'
          onClick={handleSendChatMessage}
          disabled={disabled}
          className={cm(
            'bg-outline rounded-md p-1 cursor-pointer select-none absolute right-6 bottom-[9px] z-10',
            allowSend ? 'bg-black' : 'cursor-not-allowed'
          )}
          aria-label='Send message'
        >
          <VscArrowUp className='text-white' />
        </button>
      </div>
    </div>
  )
}

export default ChatInput
